import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { CheckCircle2, XCircle, Clock, Play, HelpCircle, Ban } from 'lucide-react'
import type { BadgeTone } from '@/lib/badges'

export type UnifiedStatus =
  | 'pendiente'
  | 'ganada'
  | 'perdida'
  | 'void'
  | 'en_juego'
  | 'proximo'
  | 'cancelado'
  | 'active'
  | 'pending'
  | 'suspended'
  | 'expired'
  | 'blocked'

interface StatusInfo {
  label: string
  className: string
  icon: typeof Clock
}

const TONE_CLASS: Record<BadgeTone, string> = {
  neutral: 'border-text-dim/30 text-text-muted bg-panel-elevated/40',
  success: 'border-brand/40 text-brand bg-brand-soft/40',
  danger: 'border-danger/30 text-danger bg-danger-soft/30',
  warn: 'border-warn/30 text-warn bg-warn-soft/30',
  accent: 'border-brand/40 text-brand bg-brand-soft/40',
}

const STATUS_MAP: Record<string, StatusInfo> = {
  pendiente: {
    label: 'Pendiente',
    className: 'border-text-dim/30 text-text-muted bg-panel-elevated/40',
    icon: Clock,
  },
  ganada: {
    label: 'Acertó',
    className: 'border-brand/40 text-brand bg-brand-soft/40 shadow-[0_0_10px_rgba(167,237,32,0.06)]',
    icon: CheckCircle2,
  },
  perdida: {
    label: 'Falló',
    className: 'border-danger/30 text-danger/90 bg-danger-soft/30',
    icon: XCircle,
  },
  void: {
    label: 'Anulado',
    className: 'border-text-dim/20 text-text-dim bg-panel/30',
    icon: Ban,
  },
  en_juego: {
    label: 'En juego',
    className: 'border-brand/50 text-brand bg-brand-soft/60 animate-glow-pulse',
    icon: Play,
  },
  proximo: {
    label: 'Próximo',
    className: 'border-text-dim/30 text-text-muted bg-panel-elevated/40',
    icon: Clock,
  },
  cancelado: {
    label: 'Cancelado',
    className: 'border-text-dim/20 text-text-dim bg-panel/30',
    icon: Ban,
  },
  active: {
    label: 'Activo',
    className: 'border-brand/40 text-brand bg-brand-soft/40',
    icon: CheckCircle2,
  },
  pending: {
    label: 'Pendiente',
    className: 'border-warn/30 text-warn bg-warn-soft/30',
    icon: Clock,
  },
  suspended: {
    label: 'Suspendido',
    className: 'border-text-dim/30 text-text-muted bg-panel-elevated/30',
    icon: Ban,
  },
  expired: {
    label: 'Vencido',
    className: 'border-text-dim/30 text-text-dim bg-panel-elevated/20',
    icon: Ban,
  },
  blocked: {
    label: 'Bloqueado',
    className: 'border-danger/40 text-danger bg-danger-soft/40',
    icon: XCircle,
  },
}

export function StatusBadge({
  status,
  tone,
  className,
  children,
}: {
  status?: string | null | undefined
  tone?: BadgeTone
  className?: string
  children?: ReactNode
}) {
  // Caso de retrocompatibilidad: firma antigua que pasa 'tone' y 'children'
  if (tone && children) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border whitespace-nowrap',
          TONE_CLASS[tone] || TONE_CLASS.neutral,
          className
        )}
      >
        {children}
      </span>
    )
  }

  // Caso nuevo: traducción automática del status
  if (!status) return null
  const key = status.toLowerCase()
  const info = STATUS_MAP[key] || {
    label: status,
    className: 'border-text-dim/30 text-text-muted bg-panel-elevated/30',
    icon: HelpCircle,
  }

  const IconComponent = info.icon

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border whitespace-nowrap',
        info.className,
        className
      )}
    >
      <IconComponent className="h-3 w-3 shrink-0" />
      <span>{info.label}</span>
    </span>
  )
}
